import React from "react";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { Row, Col, Checkbox, Switch } from "antd";
import Input from "@dtci/spellbook/Input";
import Select from "@dtci/spellbook/Select";
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
// --- Hooks & Actions
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { tapApi } from "tap/api.js";
// --- Components
import { InputLabel } from "tap/components/InputLabel";
import { SectionHeader } from "tap/components/SectionHeader";
import { FormCard } from "tap/components/FormCard";
// --- Styles
import "./styles.scss";
import { usePermissions } from "tap/utils/permissions.js";

// Convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
const toInputDateFormat = (dateStr) => {
  if (!dateStr) return "";
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // Convert from MM/DD/YYYY
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
  }
  return "";
};

// Convert YYYY-MM-DD to MM/DD/YYYY for API
const toApiDateFormat = (dateStr) => {
  if (!dateStr) return "";
  // If already in MM/DD/YYYY format, return as is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // Convert from YYYY-MM-DD
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  return "";
};

// Status colors
const STATUS_COLORS = {
  green: "#4caf50",
  orange: "#ffa500",
  red: "#f44336",
  blue: "#2196f3",
  gray: "#9e9e9e",
};

// Status to color and icon mapping
const getStatusConfig = (status) => {
  const s = (status || "").toUpperCase();

  // Green statuses (checkmark)
  if (s === "ACTIVE" || s === "COMPLETE" || s === "READY" || s === "CREATED") {
    return { icon: "fa-check", color: STATUS_COLORS.green };
  }
  // Orange statuses (exclamation)
  if (s === "NEEDS_ATTENTION") {
    return { icon: "fa-exclamation", color: STATUS_COLORS.orange };
  }
  // Blue statuses (circle arrow)
  if (s === "STARTING" || s === "RUNNING" || s === "VALIDATING") {
    return { icon: "fa-arrows-rotate", color: STATUS_COLORS.blue };
  }
  // Red statuses (x)
  if (
    s === "ERROR" ||
    s === "INACTIVE" ||
    s === "INACTIVATED" ||
    s === "ABORTED" ||
    s === "INVALID" ||
    s === "FAILED"
  ) {
    return { icon: "fa-xmark", color: STATUS_COLORS.red };
  }
  // Pending status (pause)
  if (s === "PENDING") {
    return { icon: "fa-pause", color: STATUS_COLORS.orange };
  }
  // Default (no icon)
  return { icon: null, color: STATUS_COLORS.gray };
};

// Multi-select field component with add/remove functionality
const MultiSelectField = ({
  label,
  items,
  onAdd,
  onRemove,
  onChange,
  options,
  async,
  loadOptions,
  placeholder,
  noOptionsMessage,
  validationError,
  addButtonText,
  disabled,
}) => {
  // Ensure we always have at least one empty slot
  const displayItems = items.length === 0 ? [null] : items;

  return (
    <Row gutter={16} style={{ marginBottom: 20 }}>
      <Col span={24}>
        <InputLabel colProps={{ span: 24 }} label={label}>
          {displayItems.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: index < displayItems.length - 1 ? 8 : 0,
              }}
            >
              <span
                style={{
                  minWidth: 24,
                  fontWeight: 500,
                  color: "#595959",
                  fontSize: 14,
                }}
              >
                {index + 1}.
              </span>
              <div style={{ flex: 1 }}>
                <Select
                  placeholder={placeholder}
                  async={async}
                  loadOptions={loadOptions}
                  options={options}
                  isSearchable={!disabled}
                  isClearable={!disabled}
                  cacheOptions={false}
                  defaultOptions={false}
                  onChange={(value) => onChange(index, value)}
                  value={item}
                  noOptionsMessage={noOptionsMessage}
                  isDisabled={disabled}
                />
              </div>
              <Button
                iconType="fa-solid fa-trash"
                styleType="link"
                onClick={() => onRemove(index)}
                style={{ color: "#e53935", padding: 4 }}
              />
            </div>
          ))}
          {!disabled && (
            <Button
              iconType="fa-solid fa-plus"
              styleType="link"
              onClick={onAdd}
              style={{ marginTop: 8, padding: 0 }}
            >
              {addButtonText || "Add Another"}
            </Button>
          )}
          {validationError && (
            <div style={{ color: "#e53935", fontSize: 12, marginTop: 4 }}>
              {validationError}
            </div>
          )}
        </InputLabel>
      </Col>
    </Row>
  );
};

/**
 * LicensingJobForm component for create and edit modes.
 * @param {Object} props
 * @param {"create"|"edit"} props.mode - The mode of the form
 * @param {Object} props.validationErrors - Validation errors object
 * @param {Function} props.onRun - Callback for run button (edit mode only)
 * @param {Function} props.onInactivate - Callback for inactivate button (edit mode only)
 */
export function LicensingJobForm({
  mode = "create",
  validationErrors = {},
  onRun,
  onInactivate,
}) {
  // Always use licensingJobForm - it's populated with transformed data in edit mode
  const stateKey = "licensingJobForm";

  const {
    licensingJobForm,
    selectedJob,
    mediaOptions,
    territoryOptions,
    licenseeTypeOptions,
  } = useSelector((state) => state.licensingJobs);

  const { updateJobsForm } = useJobsActions();

  const { isTapAdmin } = usePermissions();

  // Always use licensingJobForm for form data
  const formData = licensingJobForm;
  const jobId = selectedJob?.id;

  const handleInputChange = (field, value) => {
    updateJobsForm({
      key: stateKey,
      data: { [field]: value },
    });
  };

  // Catalog search - debounced callback style for react-select async
  const loadCatalogOptions = debounce((inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      return callback([]);
    }
    tapApi
      .searchCatalogs(inputValue)
      .then((results) =>
        callback(
          Array.isArray(results)
            ? results.map((item) => ({
                value: item.id,
                label: item.searchableTitle,
                data: item,
              }))
            : [],
        ),
      )
      .catch(() => callback([]));
  }, 300);

  // Deal search - debounced callback style for react-select async
  const loadDealOptions = debounce((inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      return callback([]);
    }
    tapApi
      .searchDeals(inputValue)
      .then((results) =>
        callback(
          Array.isArray(results)
            ? results.map((item) => ({
                value: item.id,
                label: item.searchableTitle,
                data: item,
              }))
            : [],
        ),
      )
      .catch(() => callback([]));
  }, 300);

  // Get arrays for multi-select fields
  const getCatalogs = () => formData?.catalogs || [];
  const getNewCatalogs = () => formData?.newCatalogs || [];
  const getDeals = () => formData?.deals || [];
  const getMedias = () => formData?.medias || [];
  const getTerritories = () => formData?.territories || [];

  // Handle array field changes
  const handleArrayFieldChange = (field, index, value) => {
    const currentArray = formData?.[field] || [];
    // If array is empty but we're changing index 0 (the visual slot), initialize array
    const newArray = currentArray.length === 0 ? [null] : [...currentArray];
    if (value) {
      newArray[index] = value;
    } else {
      // If clearing, set to null so we keep the slot
      newArray[index] = null;
    }
    handleInputChange(field, newArray);
  };

  const handleArrayFieldAdd = (field) => {
    const currentArray = formData?.[field] || [];
    handleInputChange(field, [...currentArray, null]);
  };

  const handleArrayFieldRemove = (field, index) => {
    const currentArray = formData?.[field] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    handleInputChange(field, newArray);
  };

  // Get current licensee types (array of selected values)
  const getCurrentLicenseeTypes = () => {
    if (!formData?.licenseeTypes) return [];
    return formData.licenseeTypes;
  };

  // Handle licensee type checkbox change
  const handleLicenseeTypeChange = (checkedValues) => {
    handleInputChange("licenseeTypes", checkedValues);
  };

  // Handle date change - convert from YYYY-MM-DD (HTML input) to MM/DD/YYYY (API)
  const handleDateChange = (field, e) => {
    const dateValue = e.target.value;
    const formattedDate = toApiDateFormat(dateValue);
    handleInputChange(field, formattedDate);
  };

  const statusConfig = formData?.status
    ? getStatusConfig(formData.status)
    : null;

  return (
    <div className="job-form">
      {/* Title Block - Edit Mode */}
      {mode === "edit" && (
        <div className="job-form-title-block">
          <div className="job-form-title-row">
            <div className="job-form-id">Job #{jobId || "—"}</div>
            {/* Status Badge */}
            {statusConfig && (
              <div
                className="status-badge"
                style={{
                  backgroundColor: statusConfig.color + "20",
                  border: `1px solid ${statusConfig.color}40`,
                }}
              >
                {statusConfig.icon && (
                  <i
                    className={`fas ${statusConfig.icon}`}
                    style={{ fontSize: 12, color: statusConfig.color }}
                  />
                )}
                <span style={{ color: statusConfig.color }}>
                  {formData.status}
                </span>
              </div>
            )}
          </div>
          <Typography variant="T40" bold className="job-form-name">
            {formData?.description || "Untitled Job"}
          </Typography>
          {formData?.isTpr && <span className="tpr-badge">TPR Job</span>}
          {/* Actions */}
          <div className="job-form-actions">
            <Button
              iconType="fa-solid fa-play"
              styleType="link"
              onClick={onRun}
              disabled={
                formData?.status === "RUNNING" ||
                formData?.status === "RETRYING"
              }
            >
              Run Job
            </Button>
            <Button
              iconType="fa-solid fa-ban"
              styleType="link"
              onClick={onInactivate}
              style={{
                color: isTapAdmin && !formData?.isTpr ? "#e53935" : "#9e9e9e",
              }}
              disabled={!isTapAdmin || formData?.isTpr}
            >
              Inactivate
            </Button>
          </div>
        </div>
      )}

      {/* Job Details Section */}
      <SectionHeader title="Job Details" />
      <FormCard>
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={24}>
            <InputLabel colProps={{ span: 24 }} label="Description*">
              <Input
                value={formData?.description || ""}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                width="large"
                id="description"
                name="description"
                placeholder="Enter job description"
              />
              {validationErrors.description && (
                <div className="validation-error">
                  {validationErrors.description}
                </div>
              )}
            </InputLabel>
          </Col>
        </Row>

        {/* TPR Fields - Edit mode only, when isTpr is true */}
        {mode === "edit" && formData?.isTpr && (
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <div className="InputLabel">
                <h5>Rightsline Contact ID</h5>
                <Input
                  value={formData?.rlContactId || ""}
                  disabled
                  width="large"
                />
              </div>
            </Col>
            <Col span={12}>
              <div className="InputLabel">
                <h5>PMX Release ID</h5>
                <Input
                  value={formData?.pmxReleaseId || ""}
                  disabled
                  width="large"
                />
              </div>
            </Col>
          </Row>
        )}

        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <InputLabel
              colProps={{ span: 24 }}
              label={formData?.isTpr ? "TPR Start Date*" : "Start Date*"}
            >
              <Input
                type="date"
                value={toInputDateFormat(formData?.startDate)}
                onChange={(e) => handleDateChange("startDate", e)}
                width="auto"
                id="startDate"
                name="startDate"
              />
              {validationErrors.startDate && (
                <div className="validation-error">
                  {validationErrors.startDate}
                </div>
              )}
            </InputLabel>
          </Col>
          <Col span={12}>
            <InputLabel
              colProps={{ span: 24 }}
              label={formData?.isTpr ? "TPR End Date*" : "End Date*"}
            >
              <Input
                type="date"
                value={toInputDateFormat(formData?.endDate)}
                onChange={(e) => handleDateChange("endDate", e)}
                width="auto"
                id="endDate"
                name="endDate"
              />
              {validationErrors.endDate && (
                <div className="validation-error">
                  {validationErrors.endDate}
                </div>
              )}
            </InputLabel>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <InputLabel colProps={{ span: 24 }} label="Licensee Type*">
              <Checkbox.Group
                value={getCurrentLicenseeTypes()}
                onChange={handleLicenseeTypeChange}
              >
                {licenseeTypeOptions.map((option) => (
                  <div key={option.value} style={{ marginBottom: 8 }}>
                    <Checkbox value={option.value}>{option.label}</Checkbox>
                  </div>
                ))}
              </Checkbox.Group>
              {validationErrors.licenseeTypes && (
                <div className="validation-error">
                  {validationErrors.licenseeTypes}
                </div>
              )}
            </InputLabel>
          </Col>
        </Row>
      </FormCard>

      {/* Catalog Section */}
      <SectionHeader title="Catalogs" />

      <FormCard>
        {mode === "create" ? (
          /* Create mode: editable catalog selection */
          <MultiSelectField
            label="Catalog*"
            items={getCatalogs()}
            onAdd={() => handleArrayFieldAdd("catalogs")}
            onRemove={(index) => handleArrayFieldRemove("catalogs", index)}
            onChange={(index, value) =>
              handleArrayFieldChange("catalogs", index, value)
            }
            async
            loadOptions={loadCatalogOptions}
            placeholder="Search for a catalog..."
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < 3
                ? "Type at least 3 characters to search catalogs"
                : "No catalogs found"
            }
            validationError={validationErrors.catalogs}
            addButtonText="Add Catalog"
          />
        ) : (
          /* Edit mode: show existing catalogs (read-only) and add new catalogs in one section */
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={24}>
              <InputLabel colProps={{ span: 24 }} label="Catalogs">
                {/* Existing catalogs */}
                {getCatalogs().length > 0 && (
                  <>
                    {getCatalogs().map((catalog, index) => (
                      <div
                        key={`existing-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            minWidth: 24,
                            fontWeight: 500,
                            color: "#595959",
                            fontSize: 14,
                          }}
                        >
                          {index + 1}.
                        </span>
                        <div style={{ flex: 1 }}>
                          <Input
                            value={
                              catalog?.label ||
                              catalog?.titleWithId ||
                              `ID: ${catalog?.value}`
                            }
                            disabled
                            width="large"
                          />
                        </div>
                        <Button
                          iconType="fa-solid fa-trash"
                          styleType="link"
                          onClick={() =>
                            handleArrayFieldRemove("catalogs", index)
                          }
                          style={{ color: "#e53935", padding: 4 }}
                        />
                      </div>
                    ))}
                  </>
                )}

                {/* New catalogs (editable) - only show when user has added catalogs */}
                {getNewCatalogs().length > 0 &&
                  getNewCatalogs().map((item, index) => (
                    <div
                      key={`new-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          minWidth: 24,
                          fontWeight: 500,
                          color: "#595959",
                          fontSize: 14,
                        }}
                      >
                        {getCatalogs().length + index + 1}.
                      </span>
                      <div style={{ flex: 1 }}>
                        <Select
                          placeholder="Search for a catalog to add..."
                          async
                          loadOptions={loadCatalogOptions}
                          isSearchable
                          isClearable
                          cacheOptions={false}
                          defaultOptions={false}
                          onChange={(value) =>
                            handleArrayFieldChange("newCatalogs", index, value)
                          }
                          value={item}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue.length < 3
                              ? "Type at least 3 characters to search catalogs"
                              : "No catalogs found"
                          }
                        />
                      </div>
                      <Button
                        iconType="fa-solid fa-trash"
                        styleType="link"
                        onClick={() =>
                          handleArrayFieldRemove("newCatalogs", index)
                        }
                        style={{ color: "#e53935", padding: 4 }}
                      />
                    </div>
                  ))}

                <Button
                  iconType="fa-solid fa-plus"
                  styleType="link"
                  onClick={() => handleArrayFieldAdd("newCatalogs")}
                  style={{ marginTop: 8, padding: 0 }}
                >
                  Add Catalog
                </Button>
                {validationErrors.catalogs && (
                  <div style={{ color: "#e53935", fontSize: 12, marginTop: 4 }}>
                    {validationErrors.catalogs}
                  </div>
                )}
                {validationErrors.newCatalogs && (
                  <div style={{ color: "#e53935", fontSize: 12, marginTop: 4 }}>
                    {validationErrors.newCatalogs}
                  </div>
                )}
              </InputLabel>
            </Col>
          </Row>
        )}
      </FormCard>

      {/* Deal Section */}
      <SectionHeader title="Deals" />
      <FormCard>
        <MultiSelectField
          label="Deal (Optional)"
          items={getDeals()}
          onAdd={() => handleArrayFieldAdd("deals")}
          onRemove={(index) => handleArrayFieldRemove("deals", index)}
          onChange={(index, value) =>
            handleArrayFieldChange("deals", index, value)
          }
          async
          loadOptions={loadDealOptions}
          placeholder="Search for a deal..."
          noOptionsMessage={({ inputValue }) =>
            inputValue.length < 3
              ? "Type at least 3 characters to search deals"
              : "No deals found"
          }
          validationError={validationErrors.deals}
          addButtonText="Add Deal"
        />
      </FormCard>

      {/* Media Section */}
      <SectionHeader title="Media" />
      <FormCard>
        <MultiSelectField
          label="Media*"
          items={getMedias()}
          onAdd={() => handleArrayFieldAdd("medias")}
          onRemove={(index) => handleArrayFieldRemove("medias", index)}
          onChange={(index, value) =>
            handleArrayFieldChange("medias", index, value)
          }
          options={mediaOptions}
          placeholder="Select media"
          validationError={validationErrors.medias}
          addButtonText="Add Media"
        />
      </FormCard>

      {/* Territory Section */}
      <SectionHeader title="Territories" />

      <FormCard>
        <MultiSelectField
          label="Territory*"
          items={getTerritories()}
          onAdd={() => handleArrayFieldAdd("territories")}
          onRemove={(index) => handleArrayFieldRemove("territories", index)}
          onChange={(index, value) =>
            handleArrayFieldChange("territories", index, value)
          }
          options={territoryOptions}
          placeholder="Select territory"
          validationError={validationErrors.territories}
          addButtonText="Add Territory"
        />
      </FormCard>
    </div>
  );
}
